import { CardType, ControlType } from './card-model';
import { ChartType } from './chart-config.model';

/**
 * Row span for compact filter tiles (dropdown, search, date range) @ cellHeight 10.
 * One shared height keeps a filter row aligned; the tallest control (date range,
 * ~54px) sits in 8 rows with the rest of the tile being invisible padding.
 */
export const DROPDOWN_FILTER_LAYOUT_H = 8;
export const DROPDOWN_FILTER_LAYOUT_MIN_H = 6;
/** Date range filter with a title or description (one header line). */
export const DATE_RANGE_FILTER_HEADER_ONE_LINE_LAYOUT_H = 12;
/** Date range filter with both a title and a description. */
export const DATE_RANGE_FILTER_HEADER_TWO_LINES_LAYOUT_H = 14;
/**
 * Configuration for a grid item in the layout
 * @template T Type of the component being rendered, must extend BaseCardComponent
 */
export interface CardList {
  cardId: string /** Unique identifier for the grid item */;
  cardType: CardType /** Type of card to render */;
  chartType?: ChartType /** Chart type if type is 'chart' */;
  controlType?: ControlType /** Control type if type is 'filter' */;
  x: number /** GridStack X coordinate (column index) in grid units */;
  y: number /** GridStack Y coordinate (row index) in grid units */;
  w: number /** GridStack width span in columns (grid units) */;
  h: number /** GridStack height span in rows (grid units) */;
  dataSourceId?: string /** ID of the data source for this card */;
  noMove?: boolean /** Whether the item can be moved */;
  noResize?: boolean /** Whether the item can be resized */;
  locked?: boolean /** Whether the item is locked (cannot be moved or resized) */;
  minW?: number /** Minimum width in columns */;
  minH?: number /** Minimum height in rows */;
}

/**
 * Event emitted when the layout changes
 */
export interface LayoutChangeEvent {
  /** Previous state of the layout */
  previousLayout: CardList[];
  /** New state of the layout */
  newLayout: CardList[];
  /** IDs of items that changed */
  changedItemIds: string[];
  changes?: {
    incremental: Array<{
      cardId: string;
      changes: {
        from: { x: number; y: number; w: number; h: number };
        to: { x: number; y: number; w: number; h: number };
      };
    }>;
    full: CardList[];
    timestamp: string;
    changeType: "drag" | "resize";
    userId?: string;
  };
}

/**
 * Interface for layout configuration
 */
export interface LayoutConfig {
  layoutId: string /** Unique identifier for this layout */;
  pageId: string /** Reference to parent page */;
  cards: CardList[] /** Array of cards in this layout */;
  order?: number /** Order of this layout in the page */;
  revisions?: number;  /** Number of server revisions (for optimistic locking during persistence) */
}
